import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { catchError, finalize, firstValueFrom, Observable, of, switchMap } from 'rxjs';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { AziendaService } from 'src/app/services/azienda/azienda.service';
import { BilancioService } from 'src/app/services/bilancio/bilancio.service';
import { DoppiaMaterialitaService } from 'src/app/services/doppia-materialita/doppia-materialita.service';
import { MaterialitaService } from 'src/app/services/materialita/materialita.service';
import { PianoService } from 'src/app/services/piano/piano.service';
import { PolicyService } from 'src/app/services/policy/policy.service';
import { ReportVsmeService } from 'src/app/services/report-vsme/report-vsme.service';
import { SintesiService } from 'src/app/services/sintesi/sintesi.service';
import { UtenteService } from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DialogInviaMailComponent } from './dialog-invia-mail/dialog-invia-mail.component';

@Component({
  selector: 'app-dialog-crea-template-mail',
  templateUrl: './dialog-crea-template-mail.component.html',
  styleUrls: ['./dialog-crea-template-mail.component.scss'],
  standalone: false,
})
export class DialogCreaTemplateMailComponent implements AfterViewInit {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  public isInvioMail: boolean = false;
  public formTemplateMail = new FormGroup({
    id: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),

    mailInvio: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),

    mailNotifica: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),

    landingPage: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),
  });

  constructor(
    public dialogRef: MatDialogRef<DialogCreaTemplateMailComponent>,
    private readonly materialitaService: MaterialitaService,
    private readonly doppiaMaterialitaService: DoppiaMaterialitaService,
    private readonly sintesiService: SintesiService,
    private readonly utilityService: UtilityService,
    private readonly bilancioService: BilancioService,
    private readonly policyService: PolicyService,
    private readonly reportVsmeService: ReportVsmeService,

    public dialog: MatDialog,
    private readonly router: Router,
    private readonly aziendaService: AziendaService,
    private readonly pianoService: PianoService,
    private readonly utenteService: UtenteService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      idModulo: string;
      idSurvey: string;
      tipoModulo:
        | 'MATERIALITA'
        | 'SINTESI'
        | 'BILANCIO'
        | 'POLICY'
        | 'PIANO'
        | 'DOPPIA_MATERIALITA'
        | 'REPORT_VSME'
        | undefined;
      isPermessoSoloRead?: boolean;
      areaDoppiaMaterialita?: 'validatori' | 'stakeholders';
    },
  ) {}

  ngAfterViewInit(): void {
    this.datiIniziali();

    if (this.data?.isPermessoSoloRead) {
      this.formTemplateMail.disable();
    }

    this.isInvioMail = this.utenteService.isPermessoAttivo('INVIO_MAIL');
  }

  /**
   *  Metodo di salvattaggio da html form
   */
  public salvaFromHTML() {
    this.spinnerOver.show();
    this._salva()
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe((esito) => {
        if (esito) {
          this.dialogRef.close();
        }
      });
  }

  /**
   *  Metodo che salva i campi dell'oggetto DialogCreaTemplateMailComponent
   * @returns  Observable<boolean>
   */
  private _salva(): Observable<boolean> {
    if (!this.formTemplateMail.valid) {
      Object.values(this.formTemplateMail.controls).forEach((control) => {
        control.markAsTouched();
      });
      this.utilityService.openSnackBar("Compila tutti i campi prima di eseguire un'azione", '', {
        duration: 2000,
        panelClass: ['red-snackbar'],
      });

      return of(false);
    }

    // Determino il servizio da utilizzare
    let serviceCall: Observable<any> = of();

    const formValue = this.formTemplateMail.getRawValue();
    const idModulo = this.data.idModulo;

    switch (this.data.tipoModulo) {
      case 'MATERIALITA':
        serviceCall = this.materialitaService.putTemplateMail(idModulo, formValue);
        break;

      case 'DOPPIA_MATERIALITA':
        serviceCall =
          this.data.areaDoppiaMaterialita === 'validatori'
            ? this.doppiaMaterialitaService.updateTemplateMailValidazioni(idModulo, formValue)
            : this.doppiaMaterialitaService.updateTemplateMail(idModulo, formValue);
        break;

      case 'SINTESI':
        serviceCall = this.sintesiService.putTemplateMail(idModulo, formValue);
        break;

      case 'BILANCIO':
        serviceCall = this.bilancioService.putTemplateMail(idModulo, formValue);
        break;

      case 'POLICY':
        serviceCall = this.policyService.putTemplateMail(idModulo, formValue);
        break;

      case 'PIANO':
        serviceCall = this.pianoService.putTemplateMail(idModulo, formValue);
        break;

      case 'REPORT_VSME':
        serviceCall = this.reportVsmeService.putTemplateMail(idModulo, formValue);
        break;
    }

    // Effettuao la chiamata al servizio
    if (serviceCall) {
      return serviceCall.pipe(
        switchMap(() => {
          // Se la chiamata ha successo,mostro un alert di avviso,
          this.utilityService.openSnackBar('Salvataggio effettuato ', '', {
            duration: 2000,
            panelClass: ['success-snackbar'],
          });
          return of(true);
        }),
        catchError((err) => {
          // Se si verifica un errore, mostro un alert di avviso
          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: 'Errore nel salvataggio del template',
            bottoni: [{ nome_btn: 'Chiudi' }],
          });
          return of(false);
        }),
      );
    }
    return of(false);
  }

  /**
   * Restituisce un oggetto di tipo DialogInviaMailComponent
   */
  public async testMailInvio() {
    try {
      if (this.data?.isPermessoSoloRead) {
        return this._dialogInviaMail(this.formTemplateMail.controls.mailInvio.value);
      }

      const salvato = await firstValueFrom(this._salva());
      if (salvato) {
        this._dialogInviaMail(this.formTemplateMail.controls.mailInvio.value);
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   *  Restituisce un oggetto di tipo
   */
  public async testMailNotifica() {
    try {
      if (this.data?.isPermessoSoloRead) {
        return this._dialogInviaMail(this.formTemplateMail.controls.mailNotifica.value);
      }

      const salvato = await firstValueFrom(this._salva());
      if (salvato) {
        this._dialogInviaMail(this.formTemplateMail.controls.mailNotifica.value);
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   *  Se il salvataggio è andato a buon fine apre una nuova tab con l'anteprima
   */
  public async anteprima() {
    try {
      if (this.data?.isPermessoSoloRead) {
        return this._openNewTab();
      }

      const salvato = await firstValueFrom(this._salva());
      if (salvato) {
        this._openNewTab();
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   *  Dialog inserimento mail invio
   */
  private _dialogInviaMail(messaggio: string) {
    const dialogInviaMail = this.dialog.open(DialogInviaMailComponent, {
      data: {
        tipoModulo: this.data.tipoModulo,
        idModulo: this.data.idModulo,
        messaggio: messaggio,
        isPermessoSoloRead: this.data.isPermessoSoloRead,
        areaDoppiaMaterialita: this.data.areaDoppiaMaterialita,
        idSurvey: this.data.idSurvey,
      },
      panelClass: 'dialog-container',
      disableClose: false,
      width: '35%',
      maxHeight: '95%',
      autoFocus: false,
    });

    dialogInviaMail.afterClosed().subscribe((result) => {});
  }

  /**
   *  Apri una nuova tab con l'anteprima
   */
  private _openNewTab() {
    this.aziendaService.azienda.then((azienda) => {
      const idAzienda = azienda?.id;
      const { idModulo, idSurvey, tipoModulo, areaDoppiaMaterialita } = this.data || {};
      let url = '';

      if (!idModulo || !tipoModulo || !idAzienda) return;

      const queryParams: any = { idA: idAzienda };

      switch (tipoModulo) {
        case 'MATERIALITA':
          if (idSurvey) {
            url = this.router.serializeUrl(
              this.router.createUrlTree(['/compilazione-survey'], {
                queryParams: { ...queryParams, id: idModulo, idS: idSurvey },
              }),
            );
          }
          break;

        case 'DOPPIA_MATERIALITA':
          {
            const doppiaPath =
              areaDoppiaMaterialita === 'validatori'
                ? '/compilazione-validazione-iro'
                : '/compilazione-doppia-survey';

            url = this.router.serializeUrl(
              this.router.createUrlTree([doppiaPath], {
                queryParams: { ...queryParams, id: idModulo },
              }),
            );
          }
          break;

        case 'SINTESI': {
          url = this.router.serializeUrl(
            this.router.createUrlTree(['/compilazione-sintesi-sostenibilita'], {
              queryParams: { ...queryParams, id: idModulo },
            }),
          );
          break;
        }

        case 'BILANCIO': {
          url = this.router.serializeUrl(
            this.router.createUrlTree(['/compilazione-bilancio-sostenibilita'], {
              queryParams: { ...queryParams, idB: idModulo },
            }),
          );
          break;
        }

        case 'POLICY':
        case 'PIANO': {
          const prefix = tipoModulo === 'POLICY' ? 'policy' : 'piano';

          url = this.router.serializeUrl(
            this.router.createUrlTree([`/compilazione-${prefix}-sostenibilita`], {
              queryParams: { ...queryParams, idP: idModulo },
            }),
          );
          break;
        }

        case 'REPORT_VSME': {
          url = this.router.serializeUrl(
            this.router.createUrlTree(['/compilazione-report-vsme'], {
              queryParams: { ...queryParams, idR: idModulo },
            }),
          );
          break;
        }

        default:
          break;
      }

      if (url) {
        window.open(url, '_blank');
      }
    });
  }

  /**
   * Funzione che prende i dati dal service e mappa sui campi in input
   */
  private datiIniziali() {
    this.spinnerOver.show();

    let templateObservable: Observable<any> | null = null;

    const idModulo = this.data.idModulo;

    switch (this.data.tipoModulo) {
      case 'MATERIALITA':
        templateObservable = this.materialitaService.getTemplateMail(idModulo);
        break;

      case 'DOPPIA_MATERIALITA':
        templateObservable =
          this.data.areaDoppiaMaterialita === 'validatori'
            ? this.doppiaMaterialitaService.getTemplateMailValidazioni(idModulo)
            : this.doppiaMaterialitaService.getTemplateMail(idModulo);
        break;

      case 'SINTESI':
        templateObservable = this.sintesiService.getTemplateMail(idModulo);
        break;

      case 'BILANCIO':
        templateObservable = this.bilancioService.getTemplateMail(idModulo);
        break;

      case 'POLICY':
        templateObservable = this.policyService.getTemplateMail(idModulo);
        break;

      case 'PIANO':
        templateObservable = this.pianoService.getTemplateMail(idModulo);
        break;

      case 'REPORT_VSME':
        templateObservable = this.reportVsmeService.getTemplateMail(idModulo);
        break;
    }

    if (templateObservable) {
      templateObservable.pipe(finalize(() => this.spinnerOver.hide())).subscribe({
        next: (template) => this.setTemplateFormValues(template),
        error: (err) => {
          /* handle error if needed */
        },
      });
    } else {
      this.spinnerOver.hide();
    }
  }

  private setTemplateFormValues(template: any) {
    Object.keys(template).forEach((key) => {
      this.formTemplateMail.get(key)?.setValue(template[key]);
    });
  }

  get testoTemplate(): string {
    switch (this.data.tipoModulo) {
      case 'MATERIALITA':
        return 'Survey';

      case 'DOPPIA_MATERIALITA':
        return this.data.areaDoppiaMaterialita === 'validatori' ? 'Validatori' : 'Survey';

      case 'SINTESI':
        return 'Sintesi';

      case 'BILANCIO':
        return 'Report';

      case 'POLICY':
        return 'Policy';

      case 'PIANO':
        return 'Piano';

      case 'REPORT_VSME':
        return 'Report Vsme';

      default:
        return '';
    }
  }
}
